import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Logo } from "@/components/landing/Logo";
import { RichTextField } from "@/components/cms/RichTextField";
import { cms } from "@/lib/cms";
import {
  adminGetPost, adminUpdatePost, adminPublish, adminUnpublish,
  adminArchive, adminListCats, adminListTags, adminListAuthors,
  adminSchedule, absoluteUrl,
} from "@/lib/blog";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Eye, EyeOff, Archive, ImageIcon, Loader2, Plus, Trash2,
  CheckCircle2, Calendar, Search as SearchIcon, Settings, FileText,
} from "lucide-react";

const Section = ({ title, children, right, testid }) => (
  <section className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">{title}</h3>
      {right}
    </div>
    {children}
  </section>
);

export default function AdminBlogEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [cats, setCats] = useState([]);
  const [tags, setTags] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const dirtyRef = useRef(false);
  const debouncerRef = useRef(null);
  const coverInputRef = useRef(null);
  const ogInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, c, t, a] = await Promise.all([
          adminGetPost(id),
          adminListCats(),
          adminListTags(),
          adminListAuthors(),
        ]);
        if (cancelled) return;
        setPost(p);
        setCats(c);
        setTags(t);
        setAuthors(a);
      } catch (err) {
        toast.error("Could not load post");
        nav("/admin/blog");
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Debounced auto-save
  const scheduleAutoSave = (next) => {
    dirtyRef.current = true;
    if (debouncerRef.current) clearTimeout(debouncerRef.current);
    debouncerRef.current = setTimeout(() => save(next), 1200);
  };

  const update = (patch) => {
    setPost((prev) => {
      const next = { ...prev, ...patch };
      scheduleAutoSave(next);
      return next;
    });
  };
  const updateSeo = (patch) => update({ seo: { ...(post.seo || {}), ...patch } });
  const updateCover = (patch) => update({ cover_image: { ...(post.cover_image || {}), ...patch } });
  const updateCta = (patch) => update({ cta: { ...(post.cta || {}), ...patch } });

  const save = async (snapshot) => {
    if (!dirtyRef.current) return;
    const target = snapshot || post;
    if (!target) return;
    setSaving(true);
    try {
      const body = {
        title: target.title, slug: target.slug, excerpt: target.excerpt,
        content_html: target.content_html, content_json: target.content_json,
        status: target.status, published_at: target.published_at,
        cover_image: target.cover_image, category_id: target.category_id,
        tag_ids: target.tag_ids || [], author_id: target.author_id,
        seo: target.seo, faqs: target.faqs || [],
        related_post_ids: target.related_post_ids || [],
        cta: target.cta, featured: !!target.featured,
        scheduled_for: target.scheduled_for,
      };
      const fresh = await adminUpdatePost(id, body);
      setPost((p) => ({ ...p, slug: fresh.slug, reading_time_minutes: fresh.reading_time_minutes, updated_at: fresh.updated_at }));
      setLastSavedAt(new Date());
      dirtyRef.current = false;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auto-save failed");
    } finally {
      setSaving(false);
    }
  };

  const onUploadCover = async (file, key) => {
    if (!file) return;
    const r = await cms.upload(file);
    if (key === "cover") updateCover({ url: r.url });
    if (key === "og") updateSeo({ og_image: r.url });
    toast.success("Image uploaded");
  };

  const togglePublish = async () => {
    await save();
    if (post.status === "published") {
      await adminUnpublish(id);
      setPost((p) => ({ ...p, status: "draft" }));
      toast.success("Reverted to draft");
    } else {
      await adminPublish(id);
      const fresh = await adminGetPost(id);
      setPost(fresh);
      toast.success("Published");
    }
  };

  const schedulePost = async () => {
    const when = window.prompt("Schedule for (ISO 8601, e.g. 2026-07-15T09:00:00Z):", post.scheduled_for || "");
    if (!when) return;
    await save();
    await adminSchedule(id, when);
    setPost((p) => ({ ...p, status: "scheduled", scheduled_for: when }));
    toast.success("Scheduled");
  };

  const addFaq = () => update({ faqs: [...(post.faqs || []), { question: "", answer: "" }] });
  const updateFaq = (i, patch) => {
    const arr = [...(post.faqs || [])];
    arr[i] = { ...arr[i], ...patch };
    update({ faqs: arr });
  };
  const removeFaq = (i) => {
    const arr = [...(post.faqs || [])];
    arr.splice(i, 1);
    update({ faqs: arr });
  };

  if (loading || !post) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-blog-editor">
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/70 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-auto min-h-[4rem] flex flex-wrap items-center gap-2 py-2">
          <Button variant="outline" size="sm" onClick={() => nav("/admin/blog")} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-1.5" />Posts</Button>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-500 truncate font-mono">/blog/{post.slug}</div>
            <div className="text-xs text-slate-500">
              {saving ? <span className="text-emerald-700 inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />Saving…</span>
                : lastSavedAt ? <span className="text-slate-500 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-500" />Saved {lastSavedAt.toLocaleTimeString()}</span>
                : <span className="text-slate-400">Auto-saves every 1.2s</span>}
            </div>
          </div>
          <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-2 rounded-full hover:bg-slate-50 hidden sm:inline-flex" data-testid="editor-preview-link"><Eye className="h-4 w-4 inline mr-1" />Preview</a>
          <Button onClick={schedulePost} variant="outline" className="rounded-full hidden sm:inline-flex" data-testid="editor-schedule"><Calendar className="h-4 w-4 mr-1.5" />Schedule</Button>
          <Button onClick={togglePublish} className={`rounded-full font-semibold ${post.status === "published" ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-500 hover:bg-emerald-600"} text-white`} data-testid="editor-toggle-publish">
            {post.status === "published" ? <><EyeOff className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Unpublish</span></> : <><Eye className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Publish</span></>}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* Main column */}
        <div className="space-y-5 min-w-0">
          <input
            value={post.title || ""}
            onChange={(e) => update({ title: e.target.value })}
            placeholder="Post title"
            data-testid="editor-title"
            className="w-full bg-transparent text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 placeholder:text-slate-300 border-0 focus:outline-none focus:ring-0"
            style={{ fontFamily: "Outfit, sans-serif" }}
          />
          <Textarea
            value={post.excerpt || ""}
            onChange={(e) => update({ excerpt: e.target.value })}
            placeholder="Excerpt — one-liner that appears in listings and meta description fallback."
            rows={2}
            data-testid="editor-excerpt"
            className="text-base bg-transparent border-0 focus-visible:ring-0 resize-none text-slate-700 px-0"
          />

          <Section title="Content" testid="editor-content-section">
            <RichTextField
              value={post.content_html || ""}
              onChange={(html) => update({ content_html: html })}
              testid="editor-richtext"
            />
            <p className="mt-2 text-[11px] text-slate-500">Reading time: ~{post.reading_time_minutes || 1} min. Supports headings, lists, tables, images, code blocks, quotes & links.</p>
          </Section>

          <Section title="FAQ block" right={<Button variant="outline" size="sm" onClick={addFaq} data-testid="editor-add-faq"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>}>
            {(!post.faqs || post.faqs.length === 0) ? (
              <p className="text-sm text-slate-500">FAQs appear at the bottom of the post AND in JSON-LD FAQPage schema for SEO.</p>
            ) : (
              <ul className="space-y-3">
                {post.faqs.map((f, i) => (
                  <li key={i} className="rounded-lg border border-slate-200 p-3 bg-slate-50/40" data-testid={`editor-faq-${i}`}>
                    <Input value={f.question} onChange={(e) => updateFaq(i, { question: e.target.value })} placeholder="Question" className="mb-2" />
                    <Textarea value={f.answer} onChange={(e) => updateFaq(i, { answer: e.target.value })} placeholder="Answer (plain text or HTML)" rows={2} />
                    <div className="mt-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => removeFaq(i)} className="text-rose-600"><Trash2 className="h-3 w-3 mr-1" />Remove</Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Inline CTA banner" testid="editor-cta-section">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs">Show CTA inside the post</Label>
              <Switch checked={!!post.cta?.enabled} onCheckedChange={(v) => updateCta({ enabled: v })} data-testid="editor-cta-enabled" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={post.cta?.title || ""}       onChange={(e) => updateCta({ title: e.target.value })}       placeholder="Title" />
              <Input value={post.cta?.label || ""}       onChange={(e) => updateCta({ label: e.target.value })}       placeholder="Button label" />
              <Input value={post.cta?.href || ""}        onChange={(e) => updateCta({ href: e.target.value })}        placeholder="Button URL" className="sm:col-span-2" />
              <Textarea value={post.cta?.description || ""} onChange={(e) => updateCta({ description: e.target.value })} placeholder="Description" rows={2} className="sm:col-span-2" />
            </div>
          </Section>
        </div>

        {/* Sidebar */}
        <aside className="space-y-5">
          <Section title="Publish" testid="editor-publish-section">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-slate-600">Status</span>
              <span className="font-semibold capitalize">{post.status}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Featured</span>
              <Switch checked={!!post.featured} onCheckedChange={(v) => update({ featured: v })} data-testid="editor-featured" />
            </div>
            {post.status === "scheduled" && post.scheduled_for && (
              <div className="mt-2 text-xs text-blue-700 bg-blue-50 ring-1 ring-blue-200 rounded-lg px-2 py-1.5">Scheduled for {new Date(post.scheduled_for).toLocaleString()}</div>
            )}
          </Section>

          <Section title="Slug">
            <Input value={post.slug || ""} onChange={(e) => update({ slug: e.target.value })} placeholder="my-post-slug" data-testid="editor-slug" />
          </Section>

          <Section title="Cover image">
            <input ref={coverInputRef} type="file" accept="image/*" hidden onChange={(e) => onUploadCover(e.target.files?.[0], "cover")} />
            <div className="aspect-[16/9] rounded-lg overflow-hidden bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center mb-2">
              {post.cover_image?.url ? (
                <img src={absoluteUrl(post.cover_image.url)} alt={post.cover_image.alt || ""} className="h-full w-full object-cover" />
              ) : <ImageIcon className="h-7 w-7 text-slate-400" />}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => coverInputRef.current?.click()} className="flex-1" data-testid="editor-cover-upload"><ImageIcon className="h-3.5 w-3.5 mr-1" />Upload</Button>
              {post.cover_image?.url && <Button variant="ghost" size="sm" onClick={() => updateCover({ url: "" })} className="text-rose-600">Remove</Button>}
            </div>
            <Input className="mt-2" value={post.cover_image?.alt || ""} onChange={(e) => updateCover({ alt: e.target.value })} placeholder="Alt text" />
            <Input className="mt-2" value={post.cover_image?.caption || ""} onChange={(e) => updateCover({ caption: e.target.value })} placeholder="Caption (optional)" />
          </Section>

          <Section title="Taxonomy">
            <Label className="text-xs">Category</Label>
            <select value={post.category_id || ""} onChange={(e) => update({ category_id: e.target.value || null })} className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm" data-testid="editor-category">
              <option value="">— Uncategorised —</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Label className="text-xs mt-3 block">Author</Label>
            <select value={post.author_id || ""} onChange={(e) => update({ author_id: e.target.value || null })} className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm" data-testid="editor-author">
              <option value="">— No author —</option>
              {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <Label className="text-xs mt-3 block">Tags</Label>
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid="editor-tags">
              {tags.map((t) => {
                const on = (post.tag_ids || []).includes(t.id);
                return (
                  <button key={t.id} type="button"
                    onClick={() => update({ tag_ids: on ? post.tag_ids.filter((x) => x !== t.id) : [...(post.tag_ids || []), t.id] })}
                    className={`px-2 h-7 rounded-full text-[11px] font-semibold ring-1 transition-colors ${on ? "bg-emerald-500 text-white ring-emerald-500" : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100"}`}
                  >
                    #{t.name}
                  </button>
                );
              })}
              {tags.length === 0 && <span className="text-xs text-slate-500">No tags yet. <a className="font-semibold text-emerald-700" href="/admin/blog/tags">Create tags →</a></span>}
            </div>
          </Section>

          <Section title="SEO" right={<SearchIcon className="h-4 w-4 text-slate-400" />} testid="editor-seo-section">
            <Label className="text-xs">SEO title</Label>
            <Input className="mt-1" value={post.seo?.title || ""} onChange={(e) => updateSeo({ title: e.target.value })} placeholder="Defaults to post title" data-testid="editor-seo-title" />
            <Label className="text-xs mt-2 block">Meta description</Label>
            <Textarea className="mt-1" rows={3} value={post.seo?.description || ""} onChange={(e) => updateSeo({ description: e.target.value })} placeholder="155–160 characters recommended" data-testid="editor-seo-desc" />
            <Label className="text-xs mt-2 block">Focus keyword</Label>
            <Input className="mt-1" value={post.seo?.focus_keyword || ""} onChange={(e) => updateSeo({ focus_keyword: e.target.value })} placeholder="e.g. whatsapp business api" data-testid="editor-seo-focus" />
            <Label className="text-xs mt-2 block">Canonical URL</Label>
            <Input className="mt-1" value={post.seo?.canonical_url || ""} onChange={(e) => updateSeo({ canonical_url: e.target.value })} placeholder="https://wappit.com/blog/…" />
            <div className="mt-2 flex items-center justify-between">
              <Label className="text-xs">Index in search engines</Label>
              <Switch checked={post.seo?.robots_index !== false} onCheckedChange={(v) => updateSeo({ robots_index: v })} data-testid="editor-seo-robots" />
            </div>

            <div className="mt-3 border-t pt-3">
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-2">Open Graph</div>
              <Input className="mb-2" value={post.seo?.og_title || ""} onChange={(e) => updateSeo({ og_title: e.target.value })} placeholder="OG title" />
              <Textarea rows={2} className="mb-2" value={post.seo?.og_description || ""} onChange={(e) => updateSeo({ og_description: e.target.value })} placeholder="OG description" />
              <div className="flex gap-2 items-center">
                <input ref={ogInputRef} type="file" accept="image/*" hidden onChange={(e) => onUploadCover(e.target.files?.[0], "og")} />
                <Button variant="outline" size="sm" onClick={() => ogInputRef.current?.click()} className="text-xs">Upload OG image</Button>
                {post.seo?.og_image && <img src={absoluteUrl(post.seo.og_image)} alt="" className="h-9 w-16 object-cover rounded ring-1 ring-slate-200" />}
                {post.seo?.og_image && <Button variant="ghost" size="sm" onClick={() => updateSeo({ og_image: "" })} className="text-rose-600 text-xs">Remove</Button>}
              </div>
            </div>

            <div className="mt-3 border-t pt-3">
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-2">Twitter</div>
              <select value={post.seo?.twitter_card || "summary_large_image"} onChange={(e) => updateSeo({ twitter_card: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs mb-2">
                <option value="summary">summary</option>
                <option value="summary_large_image">summary_large_image</option>
              </select>
              <Input className="mb-2" value={post.seo?.twitter_title || ""} onChange={(e) => updateSeo({ twitter_title: e.target.value })} placeholder="Twitter title" />
              <Textarea rows={2} value={post.seo?.twitter_description || ""} onChange={(e) => updateSeo({ twitter_description: e.target.value })} placeholder="Twitter description" />
            </div>
          </Section>
        </aside>
      </main>
    </div>
  );
}
